import { Component, Input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { OrgDepartmentAssignment, OrgPersonAssignment } from '../../../../models/organisation-detail';
import { resolveEffectiveVisibility } from '../../../../utils/organisations.util';
import { ContactItemComponent } from '../../../../../../../contact/components/contact-item/contact-item.component';
import { DepartmentItemComponent } from '../../../../../../../contact/components/department-item/department-item.component';
import { ContactPerson } from '../../../../../../../contact/models/contact';

// Read-only mirror of what a customer would see for this organisation — excludes inactive
// contacts and any field hidden by the *effective* visibility (override ?? default). Reuses the
// same app-contact-item/app-department-item cards as the customer-facing Contacts page (see
// pages/contact) so the preview renders pixel-for-pixel what the customer sees. Purely
// presentational: it re-renders live off whatever the People/Departments sections currently
// hold, with no save of its own (organisations-design-spec.md §2.5).
@Component({
  selector: 'app-customer-preview',
  standalone: true,
  imports: [MatIcon, ContactItemComponent, DepartmentItemComponent],
  templateUrl: './customer-preview.component.html',
  styleUrl: './customer-preview.component.scss'
})
export class CustomerPreviewComponent {
  @Input() partyName = '';
  @Input() people: OrgPersonAssignment[] = [];
  @Input() departments: OrgDepartmentAssignment[] = [];

  get visiblePeople(): OrgPersonAssignment[] {
    return this.people.filter(person => person.isActive);
  }

  get visibleDepartments(): OrgDepartmentAssignment[] {
    return this.departments.filter(department => department.isActive);
  }

  toContact(person: OrgPersonAssignment): ContactPerson {
    const showPhoto = resolveEffectiveVisibility(person.showPhoto, person.defaultShowPhoto);
    const showMobile = resolveEffectiveVisibility(person.showMobilePhone, person.defaultShowMobilePhone);
    return {
      id: person.contactId.toString(),
      type: 'Person',
      title: person.title,
      sortOrder: person.sortOrder,
      name: person.displayName,
      role: person.title,
      photoUrl: showPhoto ? person.photoUrl : null,
      email: person.email,
      phone: showMobile ? person.mobilePhone : null,
      businessPhone: person.phone,
      jobTitle: person.jobTitle,
      address: person.address,
      description: null
    };
  }

  toDepartmentContact(department: OrgDepartmentAssignment): ContactPerson {
    return {
      id: department.contactId.toString(),
      type: 'Department',
      title: null,
      sortOrder: department.sortOrder,
      name: department.displayName,
      role: null,
      photoUrl: null,
      email: department.email,
      phone: department.mobilePhone,
      businessPhone: department.phone,
      jobTitle: null,
      address: department.address,
      description: department.description
    };
  }
}
